/* eslint-disable react-hooks/exhaustive-deps */
/*global window*/
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useMedia } from 'react-use'
import Datafeed from './datafeed.js';

const timeFrames = [
  {
    text: "1d",
    resolution: "1",
    description: "1 day",
  },
  {
    text: "5d",
    resolution: "5",
    description: "5 days",
  },
  {
    text: "1m",
    resolution: "120",
  },
  {
    text: "3m",
    resolution: "240",
  },
  {
    text: "1y",
    resolution: "1D",
  },
];


const TIMEZONE = {
  '-10': ['Pacific/Honolulu'],
  '-8': ['America/Anchorage', 'America/Juneau'],
  '-7': ['America/Los_Angeles', 'America/Phoenix', 'America/Vancouver'],
  '-6': ['America/Mexico_City'],
  '-5': ['America/Bogota', 'America/Chicago', 'America/Lima'],
  '-4': ['America/Caracas', 'America/New_York', 'America/Santiago', 'America/Toronto'],
  '-3': ['America/Argentina/Buenos_Aires', 'America/Sao_Paulo'],
  0: ['Atlantic/Reykjavik'],
  1: ['Africa/Casablanca', 'Africa/Lagos', 'Europe/London'],
  2: [
    'Europe/Belgrade',
    'Europe/Berlin',
    'Europe/Bratislava',
    'Europe/Brussels',
    'Europe/Budapest',
    'Europe/Copenhagen',
    'Africa/Johannesburg',
    'Europe/Luxembourg',
    'Europe/Madrid',
    'Europe/Oslo',
    'Europe/Paris',
    'Europe/Rome',
    'Europe/Stockholm',
    'Europe/Warsaw',
    'Europe/Zurich',
  ],
  3: [
    'Asia/Bahrain',
    'Europe/Athens',
    'Europe/Bucharest',
    'Africa/Cairo',
    'Europe/Helsinki',
    'Europe/Istanbul',
    'Asia/Jerusalem',
    'Asia/Kuwait',
    'Europe/Moscow',
    'Asia/Nicosia',
    'Asia/Qatar',
    'Europe/Riga',
  ],
  4: ['Asia/Dubai'],
  5: ['Asia/Karachi'],
  6: ['Asia/Almaty'],
  6.5: ['Asia/Yangon'],
  7: ['Asia/Bangkok'],
  8: ['Asia/Chongqing'],
  9: ['Asia/Tokyo'],
  9.5: ['Australia/Adelaide'],
  10: ['Australia/Brisbane'],
  11: ['Pacific/Norfolk'],
  12.75: ['Pacific/Chatham'],
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const Chart = (props) => {
  const { symbol, interval, data, ethPrice } = props;
  const [, setWindowDimensions] = useState(null);
  const offset = (-1 * new Date().getTimezoneOffset()) / 60;
  const resolvedTimezone =
    (TIMEZONE[offset] && TIMEZONE[offset][0]) ||
    // Fallback for offsets not covered in TIMEZONE map (common on mobile locales)
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'Etc/UTC';
  const below600 = useMedia('(max-width: 600px)')
  const below800 = useMedia('(max-width: 800px)')

  const [isMobile, setIsMobile] = useState(false)
  const [, setIsBook] = useState(false)
  
  // Use refs to track initialization state and prevent unnecessary re-renders
  const widgetInitialized = useRef(false);
  const currentSymbol = useRef('');
  const currentDataLength = useRef(0);

  useEffect(() => {
    setWindowDimensions(getWindowDimensions())
  }, [])

  useEffect(() => {
    setIsMobile(below600)
  }, [below600])
  useEffect(() => {
    setIsBook(below800)
  }, [below800])
  
  const chartHeight = isMobile ? 400 : 400
  
  // Memoize datafeed to prevent unnecessary recreations
  const normalizedData = useMemo(() => {
    return Array.isArray(data) ? data : (data ? [data] : []);
  }, [data]);
  
  const chartDatafeed = useMemo(() => {
    return Datafeed(normalizedData, ethPrice || 1);
  }, [normalizedData, ethPrice]);
  
  // Wait for TradingView library to load - only initialize once per symbol
  useEffect(() => {
    // Don't initialize if symbol is not provided
    if (!symbol || symbol.trim() === '') {
      return;
    }
    
    // Check if we need to reinitialize (only if symbol changed or widget doesn't exist)
    const symbolChanged = currentSymbol.current !== symbol;
    const needsReinit = !widgetInitialized.current || symbolChanged || !window.tvWidget;
    
    // If widget exists and symbol hasn't changed, don't recreate it
    if (!needsReinit && window.tvWidget) {
      return;
    }
    
    let timeoutId = null;
    let widget = null;
    
    const checkTradingView = () => {
      if (window.TradingView) {
        // Only initialize if needed
        if (needsReinit) {
          // Destroy previous widget if it exists
          if (window.tvWidget) {
            try {
              window.tvWidget.remove();
            } catch (e) {
              console.warn('Error removing previous TradingView widget:', e);
            }
            window.tvWidget = null;
          }
          
          // Update refs before creating widget
          currentSymbol.current = symbol;
          widgetInitialized.current = true;
          
        //eslint-ignore-next-line
        widget = (window.tvWidget = new window.TradingView.widget({
          symbol: symbol || 'TOKEN/USD',
          interval: interval || '30',
          fullscreen: false,
          width: "100%",
          height: "100%",
          container: 'tv_chart_container',
          datafeed: chartDatafeed,
          library_path: '/charting_library/',
          toolbar_bg: '#000000',
          autosize: true,
          overrides: {
            'paneProperties.rightMargin': 0,
            'paneProperties.background': '#000000',
            'paneProperties.backgroundType': 'solid',
            'paneProperties.backgroundGradientEndColor': '#000000',
            'paneProperties.backgroundGradientStartColor': '#000000',
            'paneProperties.vertGridProperties.color': '#666666', // Grid Vertical Lines Color
            'paneProperties.horzGridProperties.color': '#666666', // Grid Horizontal Lines Color
            'mainSeriesProperties.candleStyle.upColor': '#ffffff', // Up Candle Color
            'mainSeriesProperties.candleStyle.downColor': '#999999', // Down Candle Color
            'mainSeriesProperties.candleStyle.borderUpColor': '#ffffff', // Up Candle Border Color
            'mainSeriesProperties.candleStyle.borderDownColor': '#999999', // Down Candle Border Color
            'mainSeriesProperties.candleStyle.drawBorder': false, // Disable candle borders
            'mainSeriesProperties.minTick': '100000000,1,false',
            "scalesProperties.textSize": 18,
            "scalesProperties.showLeftScale": false,
            "mainSeriesProperties.candleStyle.hollowCandle": true,
          },
          disabled_features: ['header_symbol_search'],
          time_frames: timeFrames,
          theme: 'Dark',
          timezone: resolvedTimezone,
        }));
        
        widget.onChartReady(async () => {
          const priceScale = widget.activeChart().getPanes()[0].getRightPriceScales()[0];
          priceScale.setMode(0);

          widget.chart().setVisibleRange({
            from: new Date().getTime() / 1000 - 12 * 60 * 60,
            to: new Date().getTime() / 1000 + 12 * 60 * 60
          })
          widget.activeChart().setTimezone('UTC');

          let priceList;
          let forecastData = [];
          if (normalizedData && normalizedData.length > 0) {
            for (let i = 0; i < normalizedData.length; i++) {
              let openPrice = Number(normalizedData[i].open) * ethPrice / (10 ** 12)
              let closePrice = Number(normalizedData[i].close) * ethPrice / (10 ** 12)
              let high = Math.max(openPrice, closePrice);
              let low = Math.min(openPrice, closePrice);
              priceList = { time: Number(normalizedData[i].time), open: openPrice, high: high, low, close: closePrice };
              forecastData.push(priceList);
            }
          }
        });
        }
      } else if (!window.TradingView) {
        // If TradingView is not loaded yet, wait and check again
        timeoutId = setTimeout(checkTradingView, 100);
      }
    };
    
    checkTradingView();
    
    // Cleanup function - only cleanup timeout, not widget
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Don't cleanup widget here - it will be cleaned up when symbol changes
      // This prevents flickering when data updates
    };
  }, [symbol, interval, ethPrice, offset, resolvedTimezone]); // Removed data and chartDatafeed to prevent unnecessary re-renders
  return (
    <>
      <div 
        id="tv_chart_container" 
        style={{ 
          height: chartHeight, 
          width: '100%',
          backgroundColor: 'black',
          minHeight: '400px'
        }} 
      />
    </>
  );
};

export default Chart;
